import React from 'react';
import { atom, useAtomValue } from 'jotai';
import { genericFormAtom } from '../../../store/genericFormStore';
import resolveValuePath from '../../../Helpers/resolveValuePath';
import { useContextValues } from '../SchemaParameters';

/**
 * Where a bound value is read from.
 *
 * A term names one of these as its `scope`; the editor offers exactly this list.
 */
export const SCOPES = [
  { value: 'field', label: 'SCOPE_FIELD' },
  { value: 'record', label: 'SCOPE_RECORD' },
  { value: 'context', label: 'SCOPE_CONTEXT' },
];

/** The scope names, for checking one a layout carries. */
export const SCOPE_NAMES = SCOPES.map(({ value }) => value);

/**
 * The scopes each surface can answer for.
 *
 * A filter panel has fields but no row of its own; a detail region draws a row but takes no input; a
 * list column is decided once for the whole list, so it has neither.
 */
export const SCOPES_BY_SURFACE = {
  filters: ['field', 'context'],
  detail: ['record', 'context'],
  list: ['context'],
};

/**
 * The scopes a surface offers, all three where it is not one that says.
 *
 * @param {String} surface - The surface the entry belongs to.
 *
 * @returns {Array}
 */
export const scopesOn = (surface) => SCOPES_BY_SURFACE[surface] ?? SCOPE_NAMES;

/**
 * The scope a term reads when it names none: the record where there is one, the form otherwise.
 *
 * @param {Object} scopes - What is in scope.
 *
 * @returns {String}
 */
export const defaultScope = (scopes) => (scopes?.record ? 'record' : 'field');

/**
 * The value a term names, read out of the scope it names.
 *
 * `field` reads a sibling's current value out of the form branch, `record` reads a path into the row
 * being drawn - dotted, indexed or into its attributes - and `context` reads one of the runtime
 * values the page publishes, by name.
 *
 * @param {Object} term - The term, carrying `scope` and `key`.
 * @param {Object} scopes - What is in scope: `form`, `record` and `context`.
 *
 * @returns {*} The value, or undefined where the scope holds nothing under that key.
 */
export const scopedValue = (term, scopes) => {
  const scope = term?.scope ?? defaultScope(scopes);

  if (!term?.key) return undefined;

  if (scope === 'context') return scopes?.context?.[term.key];

  if (scope === 'record')
    return resolveValuePath(scopes?.record, term.key, scopes?.context);

  return resolveValuePath(scopes?.form, term.key, scopes?.context);
};

/** What the form scope reads where nothing would read it, so the store is never subscribed to. */
const NOTHING = atom(undefined);

/**
 * What is in scope for the fields or entries below a surface.
 *
 * The form branch is the one the surface writes to, so a term names a sibling by its own key rather
 * than by where the store keeps it. A surface with no record of its own passes none.
 *
 * `reads` says whether anything below will read the form at all. It is false by default because the
 * store is one atom holding every surface's values: reading it subscribes to writes anywhere in the
 * application, and a keystroke in one panel would redraw the fields of another. A caller with a term
 * or a dependency that names a sibling turns it on, and even then the branch is read through a
 * derived atom, so a write under a different branch stops at the value comparison.
 *
 * @param {Object} options - What the surface has.
 * @param {String} options.basePath - The surface's branch of the form store.
 * @param {Object} options.record - The row being drawn, where there is one.
 * @param {Boolean} options.reads - Whether the form branch is read.
 *
 * @returns {Object} `{ form, record, context }`.
 */
export const useBindingScopes = ({
  basePath = null,
  record = null,
  reads = false,
} = {}) => {
  const branch = React.useMemo(
    () =>
      reads
        ? atom((get) => {
            const stored = get(genericFormAtom);

            return basePath ? resolveValuePath(stored, basePath) : stored;
          })
        : NOTHING,
    [basePath, reads]
  );
  const form = useAtomValue(branch);
  const context = useContextValues();

  return React.useMemo(
    () => ({ form, record, context }),
    [form, record, context]
  );
};
